/**
 * Widget Definitions Index
 *
 * Exports all widget definitions from domain-specific modules.
 * Provides a unified registry of all available widgets.
 */

import { GENAIOPS_WIDGETS, GENAIOPS_WIDGETS_MAP } from './genaiopsWidgets';
import { AGENTOPS_WIDGETS, AGENTOPS_WIDGETS_MAP } from './agentopsWidgets';
import { HUB_WIDGETS, HUB_WIDGETS_MAP } from './hubWidgets';
import { AWS_INSIGHTS_WIDGETS, AWS_INSIGHTS_WIDGETS_MAP } from './awsInsightsWidgets';
import { PLATFORMOPS_WIDGETS, PLATFORMOPS_WIDGETS_MAP } from './platformopsWidgets';
import { DATAOPS_WIDGETS, DATAOPS_WIDGETS_MAP } from './dataopsWidgets';
import { HEALTH_WIDGETS, HEALTH_WIDGETS_MAP } from './healthWidgets';

// Re-export individual modules
export * from './genaiopsWidgets';
export * from './agentopsWidgets';
export * from './hubWidgets';
export * from './awsInsightsWidgets';
export * from './platformopsWidgets';
export * from './dataopsWidgets';
export * from './healthWidgets';

/**
 * All widgets from all domains combined into a single array
 */
export const ALL_WIDGETS = [
  ...HUB_WIDGETS,
  ...AWS_INSIGHTS_WIDGETS,
  ...PLATFORMOPS_WIDGETS,
  ...DATAOPS_WIDGETS,
  ...GENAIOPS_WIDGETS,
  ...AGENTOPS_WIDGETS,
  ...HEALTH_WIDGETS,
];

/**
 * All widgets as a map by ID for quick lookup
 */
export const ALL_WIDGETS_MAP = {
  ...HUB_WIDGETS_MAP,
  ...AWS_INSIGHTS_WIDGETS_MAP,
  ...PLATFORMOPS_WIDGETS_MAP,
  ...DATAOPS_WIDGETS_MAP,
  ...GENAIOPS_WIDGETS_MAP,
  ...AGENTOPS_WIDGETS_MAP,
  ...HEALTH_WIDGETS_MAP,
};

/**
 * Get a widget template by ID
 */
export const getWidgetTemplateById = (widgetId: string): unknown => {
  return (ALL_WIDGETS_MAP as Record<string, unknown>)[widgetId] || null;
};

/**
 * Get all widgets for a specific category
 */
export const getWidgetTemplatesByCategory = (category: string): unknown[] => {
  return ALL_WIDGETS.filter((w: { category: string }) => w.category === category);
};

/**
 * Get all widgets for a specific data source
 */
export const getWidgetTemplatesByDataSource = (dataSource: string): unknown[] => {
  return ALL_WIDGETS.filter((w: { dataSource: string }) => w.dataSource === dataSource);
};

/**
 * Check if a widget supports date filtering
 */
export const widgetSupportsDateFilter = (widgetId: string): boolean => {
  const widget = (ALL_WIDGETS_MAP as Record<string, { supportsDateFilter?: boolean }>)[widgetId];
  return widget?.supportsDateFilter ?? true;
};

const widgetExports = {
  ALL_WIDGETS,
  ALL_WIDGETS_MAP,
  getWidgetTemplateById,
  getWidgetTemplatesByCategory,
  getWidgetTemplatesByDataSource,
  widgetSupportsDateFilter,
  GENAIOPS_WIDGETS,
  AGENTOPS_WIDGETS,
  HUB_WIDGETS,
  AWS_INSIGHTS_WIDGETS,
  PLATFORMOPS_WIDGETS,
  DATAOPS_WIDGETS,
  HEALTH_WIDGETS,
};

export default widgetExports;
